<template>
  <Dialog
    :visible="visible"
    modal
    :closable="!locked"
    :show-header="false"
    :style="dialogStyle"
    :content-style="{ padding: 0, borderRadius: fullScreen ? '0' : '16px' }"
    :class="rootClass"
    append-to="body"
    @update:visible="onVisibleChange"
  >
    <!-- Custom header -->
    <header class="wizard-dialog__header">
      <div class="wizard-dialog__header-text">
        <h2 class="wizard-dialog__title">
          {{ currentTitle }}
        </h2>
        <p
          v-if="currentSubtitle"
          class="wizard-dialog__subtitle"
        >
          {{ currentSubtitle }}
        </p>
      </div>
      <button
        type="button"
        class="wizard-dialog__close"
        :disabled="locked"
        :aria-label="closeLabel"
        @click="onClose"
      >
        <i class="fa-regular fa-xmark" />
      </button>
    </header>

    <!-- Progress bar (hidden if only one phase) -->
    <div
      v-if="phases.length > 1"
      class="wizard-dialog__progress"
    >
      <div
        class="wizard-dialog__progress-fill"
        :style="{ width: progressWidth }"
      />
    </div>

    <!-- Phase viewport — only the active phase is rendered so the modal
         sizes to its natural content (no white space inflated by a taller
         sibling phase). Phase swaps use a fade transition. -->
    <div class="wizard-dialog__viewport">
      <transition
        name="wizard-dialog-fade"
        mode="out-in"
      >
        <section
          :key="phase"
          class="wizard-dialog__phase"
        >
          <slot :name="phase" />
        </section>
      </transition>
    </div>
  </Dialog>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType,
} from 'vue';
import Dialog from 'primevue/dialog';

export default defineComponent({
  name: 'WizardDialog',

  components: { Dialog },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    phase: {
      type: String,
      required: true,
    },
    phases: {
      type: Array as PropType<string[]>,
      required: true,
    },
    titles: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({}),
    },
    subtitles: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({}),
    },
    locked: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: '960px',
    },
    closeLabel: {
      type: String,
      default: 'Close',
    },
    /**
     * When true, the dialog fills the entire viewport (100vw × 100vh, no
     * rounded corners, no margin). Used for heavy multi-phase flows like
     * ChangePlanDialog where horizontal space is precious.
     */
    fullScreen: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:visible', 'update:phase', 'close'],

  setup(props, { emit }) {
    const currentIndex = computed(() => {
      const i = props.phases.indexOf(props.phase);
      return i < 0 ? 0 : i;
    });

    // Class applied directly on the <Dialog> root via `v-bind="$attrs"` —
    // PrimeVue 3.23 does NOT support the :pt API (introduced in later
    // versions), so we cannot target the mask through a pt key. We rely on
    // :class on the dialog + :has() on the mask in the CSS below.
    const rootClass = computed(() => (
      props.fullScreen
        ? 'wizard-dialog-root wizard-dialog-root--full'
        : 'wizard-dialog-root'
    ));

    const dialogStyle = computed(() => (
      props.fullScreen
        ? { width: '100vw', maxWidth: '100vw', height: '100vh', maxHeight: '100vh' }
        : { width: props.width, maxWidth: '95vw' }
    ));

    const currentTitle = computed(() => props.titles[props.phase] ?? '');
    const currentSubtitle = computed(() => props.subtitles[props.phase] ?? '');

    const progressWidth = computed(() => {
      const ratio = (currentIndex.value + 1) / props.phases.length;
      return `${Math.round(ratio * 100)}%`;
    });

    const onVisibleChange = (v: boolean) => {
      if (!v) onClose();
    };

    const onClose = () => {
      if (props.locked) return;
      emit('update:visible', false);
      emit('close');
    };

    return {
      currentTitle,
      currentSubtitle,
      progressWidth,
      rootClass,
      dialogStyle,
      onVisibleChange,
      onClose,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.wizard-dialog {
  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 28px 32px 16px;
    gap: 24px;
  }

  &__header-text {
    min-width: 0;
  }

  &__title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.02em;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: #64748b;
    margin: 4px 0 0;
    line-height: 1.5;
  }

  &__close {
    border: none;
    background: transparent;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    cursor: pointer;
    transition: all 160ms ease;
    flex-shrink: 0;

    &:hover:not(:disabled) {
      background: #f1f5f9;
      color: #0f172a;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    i {
      font-size: 1.125rem;
    }
  }

  &__progress {
    height: 3px;
    background: #f1f5f9;
    margin: 0 32px;
    border-radius: 100px;
    overflow: hidden;
  }

  &__progress-fill {
    height: 100%;
    background: $brand-color-primary;
    transition: width 360ms cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 100px;
  }

  &__viewport {
    padding: 24px 0 0;
    // Default mode: pure block layout — children stack naturally, no
    // empty space possible. FullScreen mode promotes this to flex column
    // via the unscoped rules below so the foot can pin to the bottom.
  }

  &__phase {
    padding: 0 32px;
    width: 100%;
    // Default mode: block. FullScreen overrides to flex column below.
  }
}

// Fade transition between phases.
.wizard-dialog-fade-enter-active,
.wizard-dialog-fade-leave-active {
  transition: opacity 200ms ease;
}
.wizard-dialog-fade-enter-from,
.wizard-dialog-fade-leave-to {
  opacity: 0;
}
</style>

<style lang="scss">
// Unscoped : Dialog root receives the class via :pt
// Default mode — block layout end-to-end. The dialog sizes to its content,
// nothing inflates the height, body+foot stack naturally with zero gap.
.wizard-dialog-root.p-dialog {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 80px -24px rgba(15, 23, 42, 0.32);

  .p-dialog-content {
    padding: 0 !important;
    border-radius: 0;
    // No flex, no height — let it size to its children (header + progress
    // + viewport, all natural block heights). PrimeVue's `.p-dialog-mask`
    // handles overflow scrolling if the modal exceeds the viewport.
  }
}

// ─────────────────────────────────────────────────────────────────────
// Full-screen variant — used by heavy multi-phase flows (ChangePlanDialog,
// MigrateV5Dialog).
//
// Cause root du bug : PrimeVue 3.23 NE SUPPORTE PAS le système :pt
// (introduit en versions plus récentes), donc on ne peut pas cibler le
// mask via une key passthrough. Notre classe `wizard-dialog-root--full`
// est passée via :class sur <Dialog>, ce qui via v-bind="$attrs" interne
// l'applique sur .p-dialog (vérifié dans node_modules/primevue/dialog/
// Dialog.vue:5). Pour le mask, on le cible via :has() depuis la dialog.
//
// PrimeVue 3.23 applique par défaut sur .p-dialog :
//   max-height: 90%; transform: scale(1);
// Ce sont ces deux règles qui causaient le gap de ~10% en bas du viewport.
// On les écrase avec !important.
// ─────────────────────────────────────────────────────────────────────

// Mask — neutralise le flex-centering de PrimeVue via :has() pour éviter
// qu'il ne crée un containing block décalé. La dialog elle-même est en
// position: fixed donc échappe au flex centering, mais on stretch quand
// même pour cohérence.
.p-dialog-mask:has(> .wizard-dialog-root--full),
.p-dialog-mask:has(.wizard-dialog-root--full) {
  padding: 0 !important;
  margin: 0 !important;
  align-items: stretch !important;
  justify-content: stretch !important;
}

// Dialog — calque sur PrimeVue's `.p-dialog-maximized` (qui marche en
// natif), avec ajout du reset border-radius / box-shadow. Tous les
// !important obligatoires : PrimeVue déclare son `max-height: 90%` sans
// !important mais sur un sélecteur de même spécificité (`.p-dialog`).
.wizard-dialog-root--full.p-dialog,
.p-dialog.wizard-dialog-root--full {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  min-width: 100vw !important;
  max-width: 100vw !important;
  height: 100vh !important;
  min-height: 100vh !important;
  max-height: 100vh !important;
  margin: 0 !important;
  padding: 0 !important;
  border-radius: 0 !important;
  border: none !important;
  box-shadow: none !important;
  // PrimeVue applique transform: scale(1) (et anime de scale(0.7) à
  // scale(1) à l'ouverture). Si laissé, ça peut créer un sous-pixel offset.
  // En full-screen on annule l'anim.
  transform: none !important;
  transition: none !important;
  display: flex !important;
  flex-direction: column !important;

  .p-dialog-content {
    flex: 1 1 auto !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
    min-height: 0 !important;
    max-height: none !important;
    padding: 0 !important;
    border-radius: 0 !important;
  }

  .wizard-dialog__viewport {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }

  .wizard-dialog__phase {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
}
</style>
